import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { TabsLayout } from '@/layouts/TabsLayout'
import type { TabNavigationItem } from '@/components/Tabs/TabsNavigation'
import {
  CampaignTotpStepUpProvider,
  useCampaignTotpStepUp,
} from '@/contexts/CampaignTotpStepUpContext'
import { ApiClientError } from '@/lib/api/client'
import { canManageInstitutionCampaigns } from '@/lib/campaign-totp-step-up'
import { createCampaign, fetchCampaignById } from '@/lib/campaigns'
import { stripHtmlToPlainText } from '@/lib/landing-html'
import { notifyInstitutionCampaignsUpdated } from '@/lib/sidebar-campaign-menu'
import { CampanhasLayoutProvider, useCampanhasLayout } from './CampanhasLayoutContext'
import { CampaignStatusMenu } from './CampaignStatusMenu'
import { CampaignSaveSuccessModal } from './CampaignSaveSuccessModal'
import styles from './CampanhasLayout.module.css'
import fixedSaveStyles from './CampanhasLayoutFixedSave.module.css'
import {
  CAMPAIGN_EDITOR_GROUPS,
  getCampaignEditorGroup,
} from '@/pages/CampanhasPage/editor/campaignEditorNavigation'
import editorStyles from '@/pages/CampanhasPage/editor/CampaignEditor.module.css'

const TAB_CAMPANHAS: TabNavigationItem = {
  value: 'campanhas',
  label: 'Campanhas',
  icon: 'mdi:bullhorn',
  to: '/campanhas',
}

const TAB_EDITAR: TabNavigationItem = {
  value: 'editar',
  label: 'Editar',
  icon: 'mdi:pencil',
  to: '/campanhas/editar',
}

const TAB_DASHBOARD: TabNavigationItem = {
  value: 'dashboard',
  label: 'Dashboard',
  icon: 'mdi:chart-line',
  to: '/campanhas/dashboard',
}

const CAMPANHAS_EDITAR_PATH = '/campanhas/editar'
const CAMPANHAS_DASHBOARD_PATH = '/campanhas/dashboard'
const CREATE_MODAL_QUERY_KEY = 'modal'
const CREATE_MODAL_QUERY_VALUE = 'create'
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

function normalizeCampaignSlug(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

function extractApiErrorMessage(error: ApiClientError): string {
  if (error.errors) {
    const firstEntry = Object.values(error.errors).find(
      (messages) => Array.isArray(messages) && messages.length > 0
    )
    if (firstEntry?.[0]) {
      return firstEntry[0]
    }
  }

  return error.message
}

type CreateCampaignModalProps = {
  errorMessage: string | null
  isSubmitting: boolean
  name: string
  onClose: () => void
  onNameChange: (value: string) => void
  onSlugChange: (value: string) => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  slug: string
}

function CreateCampaignModal({
  errorMessage,
  isSubmitting,
  name,
  onClose,
  onNameChange,
  onSlugChange,
  onSubmit,
  slug,
}: CreateCampaignModalProps) {
  return (
    <div
      className={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-campaign-modal-title"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose()
        }
      }}
    >
      <div className={styles.modalCard}>
        <div className={styles.modalHeader}>
          <div>
            <h2 id="create-campaign-modal-title" className={styles.modalTitle}>
              Criar campanha
            </h2>
            <p className={styles.modalSubtitle}>
              Defina o nome e o slug inicial. Depois disso, a edição continua na campanha
              criada.
            </p>
          </div>
          <button
            type="button"
            className={styles.modalClose}
            onClick={onClose}
            aria-label="Fechar modal de criação"
            disabled={isSubmitting}
          >
            <Icon icon="mdi:close" aria-hidden />
          </button>
        </div>

        <form className={styles.modalForm} onSubmit={onSubmit}>
          <div className={styles.modalBody}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="create-campaign-name">
                Nome da campanha
              </label>
              <input
                id="create-campaign-name"
                className={styles.fieldInput}
                type="text"
                value={name}
                onChange={(event) => onNameChange(event.target.value)}
                placeholder="Ex: Campanha de Natal 2026"
                autoFocus
                disabled={isSubmitting}
                required
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="create-campaign-slug">
                Slug
              </label>
              <input
                id="create-campaign-slug"
                className={styles.fieldInput}
                type="text"
                value={slug}
                onChange={(event) => onSlugChange(event.target.value)}
                placeholder="campanha-de-natal-2026"
                disabled={isSubmitting}
                required
              />
              <p className={styles.fieldHint}>
                URL pública: <strong>/campanha/{slug || 'slug-da-campanha'}</strong>
              </p>
              <p className={styles.fieldHint}>
                Use apenas letras minúsculas, números e hífens.
              </p>
            </div>

            {errorMessage ? (
              <div className={styles.modalError} role="alert">
                {errorMessage}
              </div>
            ) : null}
          </div>

          <div className={styles.modalFooter}>
            <button
              type="button"
              className={styles.modalSecondaryButton}
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={styles.modalPrimaryButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Criando...' : 'Criar campanha'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function CampanhasLayoutInner() {
  const location = useLocation()
  const navigate = useNavigate()
  const { guard, token, isCollaborator } = useAuth()
  const isEditRoute = location.pathname === CAMPANHAS_EDITAR_PATH
  const editCampaignIdParam = new URLSearchParams(location.search).get('id')
  const editCampaignId =
    isEditRoute && editCampaignIdParam && /^[1-9]\d*$/.test(editCampaignIdParam)
      ? Number(editCampaignIdParam)
      : null
  const [campaignHeader, setCampaignHeader] = useState<{
    id: number
    name: string
    subtitle: string
  } | null>(null)
  const { ensureElevated, runWithStepUp } = useCampaignTotpStepUp()
  const {
    saveTrigger,
    isSubmitting,
    hasUnsavedChanges,
    isSaveSuccessVisible,
    viewPageUrl,
    activeEditTab,
    campaignStatusControl,
  } = useCampanhasLayout()
  const canManage = canManageInstitutionCampaigns(guard, Boolean(isCollaborator))

  useEffect(() => {
    if (!editCampaignId) return
    let active = true

    void fetchCampaignById(editCampaignId)
      .then((campaign) => {
        if (!active) return
        setCampaignHeader({
          id: campaign.id,
          name: campaign.name.trim() || 'Campanhas',
          subtitle:
            stripHtmlToPlainText(campaign.prize_description) ||
            stripHtmlToPlainText(campaign.description) ||
            'Edite as informações desta campanha',
        })
      })
      .catch(() => {
        if (active) setCampaignHeader(null)
      })

    return () => {
      active = false
    }
  }, [editCampaignId])
  const openingCreateRef = useRef(false)
  const [isSavingClick, setIsSavingClick] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createSlug, setCreateSlug] = useState('')
  const [hasEditedSlug, setHasEditedSlug] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false)
  const isCreatingCampaignRef = useRef(false)
  const showingSave = isSavingClick || isSubmitting

  const resetCreateModalState = useCallback(() => {
    setCreateName('')
    setCreateSlug('')
    setHasEditedSlug(false)
    setCreateError(null)
  }, [])

  const clearCreateModalQuery = useCallback(() => {
    const params = new URLSearchParams(location.search)
    if (params.get(CREATE_MODAL_QUERY_KEY) !== CREATE_MODAL_QUERY_VALUE) return

    params.delete(CREATE_MODAL_QUERY_KEY)
    const nextSearch = params.toString()
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    )
  }, [location.pathname, location.search, navigate])

  const openCreateModal = useCallback(() => {
    if (!canManage || openingCreateRef.current) return
    openingCreateRef.current = true
    void ensureElevated().then((allowed) => {
      openingCreateRef.current = false
      if (!allowed) return
      resetCreateModalState()
      setIsCreateModalOpen(true)
    })
  }, [canManage, ensureElevated, resetCreateModalState])

  const closeCreateModal = useCallback(() => {
    if (isCreatingCampaignRef.current) return
    setIsCreateModalOpen(false)
    resetCreateModalState()
    clearCreateModalQuery()
  }, [clearCreateModalQuery, resetCreateModalState])

  useEffect(() => {
    if (!isSubmitting) {
      const sync = window.setTimeout(() => {
        setIsSavingClick(false)
      }, 0)
      return () => window.clearTimeout(sync)
    }
  }, [isSubmitting])

  useEffect(() => {
    if (!isSavingClick) return
    const fallback = setTimeout(() => {
      setIsSavingClick((prev) => (isSubmitting ? prev : false))
    }, 800)
    return () => clearTimeout(fallback)
  }, [isSavingClick, isSubmitting])

  useEffect(() => {
    if (!canManage) return

    const params = new URLSearchParams(location.search)
    const shouldOpenModal =
      params.get(CREATE_MODAL_QUERY_KEY) === CREATE_MODAL_QUERY_VALUE

    if (shouldOpenModal && !isCreateModalOpen && !openingCreateRef.current) {
      openingCreateRef.current = true
      void ensureElevated().then((allowed) => {
        openingCreateRef.current = false
        if (!allowed) {
          clearCreateModalQuery()
          return
        }
        resetCreateModalState()
        setIsCreateModalOpen(true)
      })
    }
  }, [
    canManage,
    clearCreateModalQuery,
    ensureElevated,
    isCreateModalOpen,
    location.search,
    resetCreateModalState,
  ])

  const handleSaveClick = useCallback(() => {
    if (isSavingClick || isSubmitting) return
    setIsSavingClick(true)
    if (saveTrigger) {
      saveTrigger()
      return
    }
    const form = document.getElementById('campanha-edit-form') as HTMLFormElement | null
    form?.requestSubmit()
  }, [isSavingClick, isSubmitting, saveTrigger])

  const handleCreateNameChange = useCallback(
    (value: string) => {
      setCreateName(value)
      setCreateError(null)
      if (!hasEditedSlug) {
        setCreateSlug(normalizeCampaignSlug(value))
      }
    },
    [hasEditedSlug]
  )

  const handleCreateSlugChange = useCallback((value: string) => {
    setHasEditedSlug(true)
    setCreateSlug(normalizeCampaignSlug(value))
    setCreateError(null)
  }, [])

  const validateCreateModal = useCallback((): string | null => {
    if (!canManage) {
      return 'Apenas instituições podem criar campanhas.'
    }

    if (!token) {
      return 'Sessão expirada. Faça login novamente.'
    }

    if (!createName.trim()) {
      return 'Informe o nome da campanha.'
    }

    if (!createSlug.trim()) {
      return 'Informe o slug da campanha.'
    }

    if (!SLUG_PATTERN.test(createSlug.trim())) {
      return 'Use apenas letras minúsculas, números e hífens no slug.'
    }

    return null
  }, [canManage, createName, createSlug, token])

  const handleCreateCampaignSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      if (isCreatingCampaignRef.current) return

      const validationError = validateCreateModal()
      if (validationError) {
        setCreateError(validationError)
        return
      }

      isCreatingCampaignRef.current = true
      setIsCreatingCampaign(true)
      setCreateError(null)

      try {
        const campaign = await runWithStepUp(() =>
          createCampaign(token as string, {
            name: createName.trim(),
            slug: createSlug.trim(),
            status: 'draft',
          })
        )

        notifyInstitutionCampaignsUpdated()

        setIsCreateModalOpen(false)
        resetCreateModalState()
        navigate(
          {
            pathname: CAMPANHAS_EDITAR_PATH,
            search: `?id=${campaign.id}`,
            hash: '#identidade',
          },
          { replace: true }
        )
      } catch (error) {
        if (error instanceof ApiClientError) {
          setCreateError(extractApiErrorMessage(error))
        } else {
          setCreateError('Não foi possível criar a campanha.')
        }
      } finally {
        isCreatingCampaignRef.current = false
        setIsCreatingCampaign(false)
      }
    },
    [
      createName,
      createSlug,
      navigate,
      resetCreateModalState,
      runWithStepUp,
      token,
      validateCreateModal,
    ]
  )

  const tabs = useMemo(() => {
    const base: TabNavigationItem[] = [TAB_CAMPANHAS]
    const isEditRoute = location.pathname === CAMPANHAS_EDITAR_PATH
    const isDashboardRoute = location.pathname === CAMPANHAS_DASHBOARD_PATH
    const campaignId = new URLSearchParams(location.search).get('id')
    const hasSelectedCampaign = campaignId !== null && /^[1-9]\d*$/.test(campaignId)

    if (isEditRoute && hasSelectedCampaign) {
      const group = getCampaignEditorGroup(location.hash || activeEditTab)
      return [
        { ...TAB_CAMPANHAS, active: false },
        ...CAMPAIGN_EDITOR_GROUPS.map((item) => ({
          value: item.id,
          label: item.label,
          icon: item.icon,
          to: `/campanhas/editar?id=${campaignId}#${item.firstTab}`,
          active: item.id === group.id,
        })),
      ]
    }

    if (hasSelectedCampaign && (isEditRoute || isDashboardRoute)) {
      base.push({ ...TAB_EDITAR, to: `/campanhas/editar?id=${campaignId}` })
    }
    if (isDashboardRoute && hasSelectedCampaign) {
      base.push({
        ...TAB_DASHBOARD,
        to: `/campanhas/dashboard?id=${campaignId}`,
      })
    }
    return base
  }, [activeEditTab, location.hash, location.pathname, location.search])

  const saveButtonVisualState = showingSave
    ? 'saving'
    : hasUnsavedChanges
      ? 'dirty'
      : isSaveSuccessVisible
        ? 'success'
        : 'idle'

  const viewPageButton = viewPageUrl ? (
    <a
      className={fixedSaveStyles.fixedViewPageButton}
      href={viewPageUrl}
      target="_blank"
      rel="noopener noreferrer"
      title="Abrir a página da campanha em nova aba"
    >
      <Icon icon="mdi:open-in-new" aria-hidden />
      Visualizar Página
    </a>
  ) : null

  const saveCampaignButton = (
    <button
      type="button"
      className={`${fixedSaveStyles.fixedSaveButton} ${
        saveButtonVisualState === 'dirty'
          ? fixedSaveStyles.fixedSaveButtonDirty
          : saveButtonVisualState === 'success'
            ? fixedSaveStyles.fixedSaveButtonSuccess
            : fixedSaveStyles.fixedSaveButtonIdle
      }`}
      onClick={handleSaveClick}
      disabled={saveButtonVisualState !== 'dirty'}
      aria-busy={showingSave}
      aria-live="polite"
      title={
        saveButtonVisualState === 'dirty'
          ? 'Salvar alterações da campanha'
          : saveButtonVisualState === 'success'
            ? 'Campanha salva com sucesso'
            : 'Nenhuma alteração pendente para salvar'
      }
    >
      {saveButtonVisualState === 'saving' ? (
        <>
          <Icon icon="mdi:loading" data-spin aria-hidden />
          Salvando...
        </>
      ) : saveButtonVisualState === 'success' ? (
        <>
          <Icon
            icon="mdi:check-circle-outline"
            className={fixedSaveStyles.fixedSaveButtonSuccessIcon}
            aria-hidden
          />
          Salvo com sucesso
        </>
      ) : (
        <>
          <Icon icon="mdi:content-save-outline" aria-hidden />
          Salvar campanha
        </>
      )}
    </button>
  )

  const fixedActionBar =
    isEditRoute && typeof document !== 'undefined'
      ? createPortal(
          <div className={fixedSaveStyles.fixedSaveBar}>
            {viewPageButton}
            {canManage ? saveCampaignButton : null}
          </div>,
          document.body
        )
      : null

  const headerAction = useMemo(() => {
    if (!canManage) return null
    if (location.pathname === CAMPANHAS_EDITAR_PATH) {
      return (
        <div className={editorStyles.headerActions}>
          <CampaignStatusMenu control={campaignStatusControl} disabled={showingSave} />
          <button
            type="button"
            className={styles.saveButton}
            onClick={handleSaveClick}
            disabled={showingSave || !hasUnsavedChanges}
            aria-busy={showingSave}
          >
            {showingSave ? (
              <>
                <Icon icon="mdi:loading" data-spin aria-hidden />
                Salvando...
              </>
            ) : (
              <>
                <Icon icon="mdi:content-save-outline" aria-hidden />
                Salvar campanha
              </>
            )}
          </button>
        </div>
      )
    }
    return (
      <button
        type="button"
        className={styles.createButton}
        onClick={openCreateModal}
        aria-haspopup="dialog"
      >
        <Icon icon="mdi:plus" aria-hidden />
        Criar Campanha
      </button>
    )
  }, [
    canManage,
    handleSaveClick,
    hasUnsavedChanges,
    location.pathname,
    openCreateModal,
    showingSave,
    campaignStatusControl,
  ])

  return (
    <>
      <TabsLayout
        title={
          isEditRoute && campaignHeader?.id === editCampaignId
            ? campaignHeader.name
            : 'Campanhas'
        }
        titleClassName={isEditRoute ? styles.campaignHeaderTitle : undefined}
        subtitle={
          isEditRoute && campaignHeader?.id === editCampaignId
            ? campaignHeader.subtitle
            : 'Gerencie suas campanhas promocionais'
        }
        subtitleClassName={isEditRoute ? styles.campaignHeaderSubtitle : undefined}
        tabs={tabs}
        tabsClassName={isEditRoute ? editorStyles.primaryTabs : undefined}
        headerAction={headerAction}
      />

      {fixedActionBar}

      {isEditRoute && isSaveSuccessVisible && typeof document !== 'undefined'
        ? createPortal(<CampaignSaveSuccessModal />, document.body)
        : null}

      {isCreateModalOpen ? (
        <CreateCampaignModal
          errorMessage={createError}
          isSubmitting={isCreatingCampaign}
          name={createName}
          onClose={closeCreateModal}
          onNameChange={handleCreateNameChange}
          onSlugChange={handleCreateSlugChange}
          onSubmit={handleCreateCampaignSubmit}
          slug={createSlug}
        />
      ) : null}
    </>
  )
}

export function CampanhasLayout() {
  return (
    <CampaignTotpStepUpProvider>
      <CampanhasLayoutProvider>
        <CampanhasLayoutInner />
      </CampanhasLayoutProvider>
    </CampaignTotpStepUpProvider>
  )
}
